import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, Mail, Sparkles } from 'lucide-react';
import InstallAppButton from '../components/InstallAppButton';
import './site.css';

const NAV_LINKS = [
  { to: '/', label: 'Accueil' },
  { to: '/a-propos', label: 'À propos' },
  { to: '/services', label: 'Services' },
  { to: '/medecins', label: 'Médecins' },
  { to: '/rendez-vous', label: 'Rendez-vous' },
  { to: '/teleconsultation', label: 'Téléconsultation' },
  { to: '/entreprises', label: 'Entreprises' },
  { to: '/journal', label: 'Journal' },
  { to: '/contact', label: 'Contact' },
];

export const PHONE_DISPLAY = '+243 840 061 625';
export const PHONE_TEL = '+243840061625';
export const PORTAL_URL = '/portail';

// Informations légales officielles (SARLU)
export const LEGAL = {
  companyName: 'Centre Médical la Trinité SARLU',
  formeJuridique: 'SARLU (Société à Responsabilité Limitée Unipersonnelle)',
  rccm: 'CD/MAT/RCCM/23-B-0656',
  idnat: '10-Q8601-N30379M',
  nif: 'A1600716X',
  autorisationMSP: 'N° MSP 1257/22/012 du 08/03/2016',
  email: 'contact@cmlatrinite-matadi.com',
  addressMatadi: '1175, Avenue Nkala-nkala, Quartier Ville Haute, Commune de Matadi',
  addressBoma: '1019, Avenue du Fleuve, Quartier Fischer, Commune de Nzadi (Boma)',
};

export default function SiteLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="cmt-site">
      <header className="site-header" data-testid="site-header">
        <div className="site-header-inner">
          <Link to="/" className="site-brand" data-testid="site-brand">
            <img src="/cmt_logo.png" alt="Logo Centre Médical la Trinité" />
            <span className="site-brand-name">
              Centre Médical la Trinité
              <small>Matadi · RD Congo</small>
            </span>
          </Link>

          <nav className="site-nav" data-testid="site-nav">
            {NAV_LINKS.map(l => (
              <NavLink key={l.to} to={l.to} end={l.to === '/'}>{l.label}</NavLink>
            ))}
          </nav>

          <div className="site-header-actions">
            <InstallAppButton className="site-btn site-btn-ghost site-header-install-cta" label="Télécharger l'app" />
            <a
              href={`${PORTAL_URL}?page=doctor-login`}
              className="site-btn site-btn-ghost site-header-doctor-cta"
              data-testid="site-header-doctor-btn"
            >
              Espace Médecin
            </a>
            <a href={PORTAL_URL} className="site-btn site-btn-primary site-header-cta" data-testid="site-header-portal-btn">
              Espace Patient
            </a>
          </div>

          <button
            className="site-burger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu de navigation"
            data-testid="site-burger"
          >
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {menuOpen && (
          <div className="site-mobile-menu" data-testid="site-mobile-menu">
            {NAV_LINKS.map(l => (
              <NavLink key={l.to} to={l.to} end={l.to === '/'}>{l.label}</NavLink>
            ))}
            <a href={PORTAL_URL} className="site-btn site-btn-primary" data-testid="site-mobile-portal-btn">
              Espace Patient
            </a>
            <a
              href={`${PORTAL_URL}?page=doctor-login`}
              className="site-btn site-btn-ghost"
              data-testid="site-mobile-doctor-btn"
            >
              Espace Médecin
            </a>
            <InstallAppButton className="site-btn site-btn-outline" label="Télécharger l'app" />
          </div>
        )}
      </header>

      <Outlet />

      {/* Emplacement du futur Assistant IA CMT */}
      <div className="site-assistant" data-testid="site-assistant">
        {assistantOpen && (
          <div className="site-assistant-popover" data-testid="site-assistant-popover">
            <h4><Sparkles size={16} /> Assistant CMT</h4>
            <p>
              Votre assistant intelligent arrive bientôt : il répondra à vos questions,
              vous orientera vers le bon service et vous aidera à prendre rendez-vous.
            </p>
            <span className="site-assistant-soon">Bientôt disponible</span>
          </div>
        )}
        <button
          className="site-assistant-btn"
          onClick={() => setAssistantOpen(!assistantOpen)}
          aria-label="Assistant CMT (bientôt disponible)"
          data-testid="site-assistant-btn"
        >
          <Sparkles size={22} />
        </button>
      </div>

      <footer className="site-footer" data-testid="site-footer">
        <div className="site-footer-inner">
          <div className="site-footer-brand">
            <img src="/cmt_logo.png" alt="Logo CMT" />
            <p><strong>Centre Médical la Trinité SARLU</strong></p>
            <p style={{ fontSize: 13, opacity: 0.85 }}>
              Une médecine spécialisée fondée sur la confiance, l&apos;excellence et l&apos;innovation.
            </p>
            <p style={{ fontSize: 11, opacity: 0.75, marginTop: 8, lineHeight: 1.6 }}>
              RCCM&nbsp;: {LEGAL.rccm}<br />
              Idnat&nbsp;: {LEGAL.idnat}<br />
              NIF&nbsp;: {LEGAL.nif}<br />
              Autorisation MSP&nbsp;: {LEGAL.autorisationMSP}
            </p>
          </div>
          <div>
            <h4>Navigation</h4>
            <Link to="/a-propos">À propos</Link>
            <Link to="/services">Nos services</Link>
            <Link to="/medecins">Nos médecins</Link>
            <Link to="/entreprises">Entreprises & Partenaires</Link>
            <Link to="/journal">Journal Médical</Link>
            <Link to="/galerie">Galerie photo</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <div>
            <h4>Patients</h4>
            <a href={`${PORTAL_URL}?page=booking`}>Prendre rendez-vous</a>
            <Link to="/teleconsultation">Téléconsultation</Link>
            <a href={`${PORTAL_URL}?page=login`}>Espace patient</a>
          </div>
          <div>
            <h4>Personnel médical</h4>
            <a href={`${PORTAL_URL}?page=doctor-login`}>Espace Médecin</a>
            <a href={`${PORTAL_URL}?page=admin-login`}>Espace Administration</a>
          </div>
          <div>
            <h4>Contact</h4>
            <a href={`tel:${PHONE_TEL}`}><Phone size={13} style={{ display: 'inline', marginRight: 6 }} />{PHONE_DISPLAY}</a>
            <a href="mailto:contact@cmlatrinite-matadi.com"><Mail size={13} style={{ display: 'inline', marginRight: 6 }} />contact@cmlatrinite-matadi.com</a>
            <span style={{ display: 'flex', gap: 6, fontSize: '0.9rem', color: 'rgba(255,255,255,0.75)' }}>
              <MapPin size={15} style={{ flexShrink: 0, marginTop: 3 }} />
              Matadi & Boma, Kongo Central — RDC
            </span>
            <p style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
              Consultations : lundi – samedi, 8h – 17h<br />
              <strong>Urgences : 24h/24, 7j/7</strong>
            </p>
          </div>
        </div>
        <div className="site-footer-bottom">
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 1rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10, fontSize: 12, alignItems: 'center' }}>
            <span>© {new Date().getFullYear()} Centre Médical la Trinité SARLU — CMT Health Platform. Tous droits réservés.</span>
            <div style={{ display: 'flex', gap: 12, opacity: 0.85 }}>
              <Link to="/mentions-legales" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'underline' }}>Mentions légales</Link>
              <Link to="/confidentialite" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'underline' }}>Confidentialité</Link>
            </div>
          </div>
          <p style={{ maxWidth: 1200, margin: '10px auto 0', padding: '0 1rem', fontSize: 11, opacity: 0.7, lineHeight: 1.6 }}>
            Les informations médicales publiées sur ce site ont un caractère général et ne remplacent pas un avis médical personnalisé.
            Elles ne sauraient constituer un diagnostic ni un traitement.
            En cas d&apos;urgence, veuillez contacter le Centre ou les services de secours compétents.
          </p>
        </div>
      </footer>
    </div>
  );
}
